'use client'

import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import * as Select from '@radix-ui/react-select'
import { CategoryTreeItem } from '@cerifi/ecomm/bigcommerce/types/category'
import { Container } from '@cerifi/ui/components/container'
import { ChevronDownIcon } from '@cerifi/ui/components/icons'
import { cn } from '@cerifi/ui/utils/cn'

interface CategoryNavProps {
  categoryTree: CategoryTreeItem[] | null
  isShopAll: boolean
}

const Nav = ({
  pathname,
  nav,
  isShopAll,
  parent,
}: {
  pathname: string
  nav: CategoryTreeItem[]
  isShopAll: boolean
  parent?: CategoryTreeItem
}) => {
  const router = useRouter()

  return (
    <nav
      className="sticky top-0 z-20 bg-white"
      style={{ boxShadow: '0px 4px 10px 0px rgba(120, 116, 116, 0.05)' }}
    >
      <Select.Root
        aria-label="Category"
        onValueChange={(path) => router.push(path)}
      >
        <Select.Trigger className="sm:hidden flex px-8 py-4 font-medium w-full items-center justify-between">
          <Select.Value>
            {isShopAll
              ? 'Shop All'
              : nav.find((x) => pathname.startsWith(x.path.replace(/\/$/, '')))
                  ?.name}
          </Select.Value>
          <Select.Icon>
            <ChevronDownIcon width={20} height={20} />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content
            position="popper"
            className={cn('-ml-2.5 w-screen bg-white pb-3')}
            style={{
              zIndex: 1000,
              boxShadow: '0px 4px 10px 0px rgba(120, 116, 116, 0.05)',
            }}
          >
            <Select.Viewport>
              {parent && (
                <Select.Item
                  className="py-3 px-8 cursor-pointer hover:bg-neutral-50"
                  value={parent.path}
                >
                  <Select.ItemText>Shop All</Select.ItemText>
                </Select.Item>
              )}
              {nav.map((x) => (
                <Select.Item
                  className="py-3 px-8 cursor-pointer hover:bg-neutral-50"
                  value={x.path}
                  key={x.entityId}
                >
                  <Select.ItemText>{x.name}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
            <Select.ScrollDownButton className="flex h-[1.5625rem] cursor-default items-center justify-center bg-white">
              <ChevronDownIcon />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      <Container className="hidden sm:block mx-auto p-8">
        <div className="flex items-center">
          <ul className="mr-6 mt-0 flex flex-row space-x-8 text-base font-medium">
            {parent && (
              <li>
                <Link
                  href={`${parent.path}`}
                  className={cn(
                    'hover:underline',
                    isShopAll && 'text-neutral-900 font-bold'
                  )}
                >
                  Shop All
                </Link>
              </li>
            )}
            {nav.map((x) => {
              const currentPath = x.path.replace(/\/$/, '') // remove trailing slash, it isn't present in pathname
              const isActive = pathname.startsWith(`${currentPath}`)
              return (
                <li key={x.entityId}>
                  <Link
                    href={`${x.path}`}
                    className={cn(
                      'hover:underline',
                      isActive && 'text-neutral-900 font-bold'
                    )}
                    aria-current={isActive ? 'page' : 'false'}
                  >
                    {x.name}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      </Container>
    </nav>
  )
}

/**
 * TODO: Render siblings if at lowest level
 * If current nav does not have children, render siblings instead
 * @param param
 * @returns
 */
export const CategoryNav = ({ categoryTree, isShopAll }: CategoryNavProps) => {
  const pathname = usePathname()

  if (!categoryTree) return null

  if (categoryTree?.length === 1 && categoryTree[0]?.children) {
    return (
      <Nav
        pathname={String(pathname)}
        isShopAll={isShopAll}
        nav={categoryTree[0].children}
        parent={categoryTree[0]}
      />
    )
  }

  return (
    <Nav pathname={String(pathname)} nav={categoryTree} isShopAll={isShopAll} />
  )
}
